(() => {
  'use strict';

  // v2 (2026-09-15): anonymous first-party attribution unless the visitor declined or sends a Global Privacy Control
  // signal (owner decisions ~01:5x and 02:15 JST).
  // Accepts the independent site's reading path / active time fields, and also records campaign parameters
  // when a visitor lands on Shopify directly from an ad or AI answer without passing through the site.
  const params = new URLSearchParams(window.location.search);
  const html = document.documentElement;
  const MAX_VALUE_LENGTH = 500;
  const STORAGE_KEY = 'ofk_shopify_attribution_payload_v1';
  const CONSENT_COOKIE = 'ofk_consent_v1';
  const QUERY_KEYS = [
    'ofk_visit_id',
    'ofk_entry_path',
    'ofk_referrer_host',
    'ofk_entry_market',
    'ofk_entry_locale',
    'ofk_first_seen_at',
    'ofk_handoff_source',
    'ofk_handoff_market',
    'ofk_handoff_locale',
    'ofk_placement',
    'ofk_attribution_version',
    'ofk_consent',
    'ofk_first_source',
    'ofk_first_medium',
    'ofk_first_campaign',
    'ofk_first_term',
    'ofk_first_content',
    'ofk_last_source',
    'ofk_last_medium',
    'ofk_last_campaign',
    'ofk_last_term',
    'ofk_last_content',
    'ofk_source_class',
    'ofk_site_first_path',
    'ofk_site_last_path',
    'ofk_site_path',
    'ofk_site_page_count',
    'ofk_site_elapsed_sec',
    'ofk_site_active_sec',
    'ofk_site_max_scroll',
    'ofk_mt',
    'ofk_ag',
    'ofk_internal',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'dclid',
    'gad_source',
    'gad_campaignid',
    'oppref',
    'fbclid',
    '_gl',
  ];
  const LANDING_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'gbraid',
    'wbraid',
    'gad_source',
    'gad_campaignid',
    'oppref',
    'ofk_mt',
    'ofk_ag',
    'ofk_internal',
  ];

  const clean = (value, maxLength = MAX_VALUE_LENGTH) =>
    String(value || '').replace(/[\u0000-\u001f\u007f]/gu, '').trim().slice(0, maxLength);

  const validVisitId = (value) => /^ofk_[a-z0-9_-]+$/iu.test(clean(value, 64));

  const cookieChoice = () => {
    const entry = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${CONSENT_COOKIE}=`));
    const value = entry?.slice(CONSENT_COOKIE.length + 1);
    return value === 'granted' || value === 'denied' ? value : null;
  };

  const incomingConsent = clean(params.get('ofk_consent'), 16);
  // A link must never grant consent on the visitor's behalf. Only the privacy-preserving
  // denial is honoured from the URL; a real "granted" choice arrives via the shared cookie.
  if (incomingConsent === 'denied') {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const base = `${CONSENT_COOKIE}=${incomingConsent}; Path=/; Max-Age=15552000; SameSite=Lax${secure}`;
    document.cookie = base;
    if (/(^|\.)ofutonkobo\.com$/iu.test(window.location.hostname)) {
      document.cookie = `${base}; Domain=.ofutonkobo.com`;
    }
  }

  const gpc = navigator.globalPrivacyControl === true || incomingConsent === 'gpc';
  const consent = incomingConsent === 'denied' || gpc ? 'denied' : cookieChoice();
  // Internal-device marker: cookie ofk_internal=1 (set on www via ?ofk_internal=on) or ?ofk_internal=1 on a bookmarked shop URL.
  const internalDevice = /(?:^|;\s*)ofk_internal=1(?:;|$)/u.test(document.cookie) || clean(params.get('ofk_internal'), 4) === '1';
  html.dataset.ofkConsent = gpc ? 'gpc' : consent || 'unset';
  if (consent === 'denied') {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage may be unavailable under the visitor's browser policy.
    }
    return;
  }

  const readStoredAttributes = () => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || 'null');
      return stored && (validVisitId(stored.ofk_visit_id) || stored.ofk_shopify_landing_path) ? stored : null;
    } catch {
      return null;
    }
  };

  const referrerHost = () => {
    if (!document.referrer) return '';
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin ? '' : clean(referrer.hostname, 253);
    } catch {
      return '';
    }
  };

  const incomingVisitId = clean(params.get('ofk_visit_id'), 64);
  const hasIncomingHandoff = validVisitId(incomingVisitId);
  const storedAttributes = readStoredAttributes();
  const landingTouch = Object.fromEntries(
    LANDING_KEYS.flatMap((key) => {
      const value = clean(params.get(key));
      return value ? [[key, value]] : [];
    })
  );
  const hasLandingTouch = !hasIncomingHandoff && Object.keys(landingTouch).length > 0;
  if (!hasIncomingHandoff && !hasLandingTouch && !storedAttributes) return;

  let attributes;
  if (hasIncomingHandoff) {
    attributes = Object.fromEntries(QUERY_KEYS.map((key) => [key, '']));
    for (const key of QUERY_KEYS) {
      const value = clean(params.get(key));
      if (value) attributes[key] = value;
    }
    attributes.ofk_visit_id = incomingVisitId;
  } else if (hasLandingTouch && !(storedAttributes && validVisitId(storedAttributes.ofk_visit_id))) {
    // Direct landing on Shopify (no site handoff in this tab): keep the first landing, refresh the last touch.
    attributes = storedAttributes ? { ...storedAttributes } : {};
    if (!attributes.ofk_shopify_landing_path) {
      attributes.ofk_shopify_landing_path = clean(window.location.pathname, 220);
      attributes.ofk_shopify_referrer_host = referrerHost();
      attributes.ofk_shopify_first_seen_at = new Date().toISOString();
    }
    for (const key of LANDING_KEYS) {
      if (landingTouch[key]) attributes[key] = landingTouch[key];
    }
    attributes.ofk_attribution_version = '2';
  } else {
    attributes = { ...storedAttributes };
  }

  attributes.ofk_shopify_market = clean(html.dataset.ofkMarket, 12);
  attributes.ofk_shopify_locale = clean(html.dataset.ofkLocale, 24);
  attributes.ofk_shopify_consent = consent || 'unset';
  if (internalDevice) attributes.ofk_internal = '1';
  attributes.ofk_market = clean(
    params.get('ofk_handoff_market') || attributes.ofk_market || html.dataset.ofkMarket,
    12
  );
  attributes.ofk_locale = clean(
    params.get('ofk_handoff_locale') || attributes.ofk_locale || html.dataset.ofkLocale,
    24
  );

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attributes));
  } catch {
    // The current page can still sync the cart when storage is unavailable.
  }

  const root = window.Shopify?.routes?.root || '/';
  const endpoint = `${root.replace(/\/?$/u, '/')}cart/update.js`;
  html.dataset.ofkAttributionState = 'syncing';


  // Batch 0-1: bound optional metadata without changing native cart contents.
  const TOTAL_BUDGET_MS = 2500;
  const ATTEMPT_BUDGET_MS = 1200;
  const monotonicNow = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? () => performance.now()
    : () => Date.now();
  const deadline = monotonicNow() + TOTAL_BUDGET_MS;
  let terminal = false;
  let pageActive = true;
  let totalTimer;
  let cancelAttempt = null;
  let cancelSleep = null;
  let resolveReady;
  let intent = null;
  let replayingForm = null;
  const ready = new Promise((resolve) => { resolveReady = resolve; });
  window.ofkAttributionReady = ready;

  const remaining = () => Math.max(0, deadline - monotonicNow());
  const currentlyDenied = () => navigator.globalPrivacyControl === true ||
    incomingConsent === 'denied' || incomingConsent === 'gpc' || cookieChoice() === 'denied';
  const finish = (success, error) => {
    if (terminal) return;
    terminal = true;
    window.clearTimeout(totalTimer);
    if (cancelSleep) cancelSleep();
    if (cancelAttempt) cancelAttempt();
    html.dataset.ofkAttributionState = success ? 'ready' : 'error';
    resolveReady(success);
    try {
      document.dispatchEvent(new CustomEvent(
        success ? 'ofk:shopify-attribution-synced' : 'ofk:shopify-attribution-error',
        { detail: success ? { visitId: attributes.ofk_visit_id || '' } : { error: String(error || '') } }
      ));
    } catch {
      // An optional measurement listener must not leave ready rejected or pending.
    }
  };

  const sleep = (milliseconds) => new Promise((resolve) => {
    if (terminal || remaining() <= 0) { resolve(false); return; }
    let settled = false;
    const done = (elapsed) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (cancelSleep === cancel) cancelSleep = null;
      resolve(elapsed);
    };
    const cancel = () => done(false);
    const timer = window.setTimeout(() => done(true), Math.min(milliseconds, remaining()));
    cancelSleep = cancel;
  });

  const attempt = () => new Promise((resolve) => {
    if (terminal || remaining() <= 0 || currentlyDenied()) {
      resolve({ ok: false, error: new Error('Cart attribution task no longer active') });
      return;
    }
    let settled = false;
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const done = (result, abort) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (cancelAttempt === cancel) cancelAttempt = null;
      if (abort && controller) {
        try { controller.abort(); } catch { /* The deadline still applies without abort support. */ }
      }
      resolve(result);
    };
    const cancel = () => done({ ok: false, error: new Error('Cart attribution task ended') }, true);
    const timer = window.setTimeout(
      () => done({ ok: false, error: new Error('Cart attribution attempt timed out') }, true),
      Math.min(ATTEMPT_BUDGET_MS, remaining())
    );
    cancelAttempt = cancel;
    // The operation includes body reading. Late results have no state/intent side effects.
    Promise.resolve().then(() => {
      if (settled || terminal || remaining() <= 0 || currentlyDenied()) {
        throw new Error('Cart attribution task no longer active');
      }
      return fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
        ...(controller ? { signal: controller.signal } : {}),
      });
    }).then((response) => {
      if (settled || terminal) return;
      if (!response.ok) throw new Error(`Cart attribution sync failed with ${response.status}`);
      return response.json();
    }).then(() => {
      if (settled || terminal) return;
      if (remaining() <= 0 || currentlyDenied()) {
        done({ ok: false, error: new Error('Cart attribution result arrived after task ended') }, true);
        return;
      }
      done({ ok: true }, false);
    }).catch((error) => done({ ok: false, error }, false));
  });

  totalTimer = window.setTimeout(
    () => finish(false, new Error('Cart attribution ready budget exhausted')),
    TOTAL_BUDGET_MS
  );
  const syncCart = async () => {
    let lastError;
    for (const delay of [0, 250, 750]) {
      if (terminal) return;
      if (remaining() <= 0 || currentlyDenied()) {
        finish(false, new Error('Cart attribution task no longer active'));
        return;
      }
      if (delay && !(await sleep(delay))) return;
      if (terminal) return;
      if (remaining() <= 0 || currentlyDenied()) {
        finish(false, new Error('Cart attribution task no longer active'));
        return;
      }
      const result = await attempt();
      if (terminal) return;
      if (result.ok) { finish(true); return; }
      lastError = result.error;
    }
    finish(false, lastError);
  };
  syncCart().catch((error) => finish(false, error));

  // Snapshot only native commerce state in memory; optional attributes are not commerce.
  // If a browser cannot safely snapshot/replay a form, leave its first event native.
  const optionalFormKeys = new Set(Object.keys(attributes).map((key) => `attributes[${key}]`));
  const commerceSnapshot = (heldForm = null) => {
    const forms = [...document.querySelectorAll('form[action]')].filter((form) => {
      if (!(form instanceof HTMLFormElement)) return false;
      try {
        const action = new URL(form.action, window.location.href);
        return action.origin === window.location.origin && /\/(?:cart|checkout)\/?$/u.test(action.pathname);
      } catch { return false; }
    });
    if (heldForm && !forms.includes(heldForm)) forms.push(heldForm);
    if (forms.length && typeof FormData !== 'function') return null;
    try {
      return forms.map((form) => ({
        form,
        action: form.action,
        method: form.method,
        target: form.target,
        noValidate: form.noValidate,
        enctype: form.enctype,
        fields: JSON.stringify([...new FormData(form)].filter(([key]) => !optionalFormKeys.has(key))),
      }));
    } catch { return null; }
  };
  const sameCommerce = (captured, heldForm) => {
    const current = commerceSnapshot(heldForm);
    return current !== null && captured.length === current.length &&
      captured.every((before, index) => {
        const after = current[index];
        return before.form === after.form && before.form.isConnected &&
          before.action === after.action && before.method === after.method &&
          before.target === after.target && before.noValidate === after.noValidate &&
          before.enctype === after.enctype && before.fields === after.fields;
      });
  };
  const releaseIntent = () => {
    const held = intent;
    if (!held) return;
    intent = null; // Take the only continuation token before invoking native handlers.
    if (!pageActive || held.page !== window.location.href ||
        !sameCommerce(held.commerce, held.kind === 'form' ? held.form : null)) return;
    if (held.kind === 'form') {
      const { form, submitter } = held;
      if (!form.isConnected || form.action !== held.action || form.method !== held.method ||
          form.target !== held.target || form.noValidate !== held.noValidate ||
          form.enctype !== held.enctype || typeof form.requestSubmit !== 'function') return;
      if (submitter && (!submitter.isConnected || submitter.disabled || submitter.form !== form ||
          submitter.name !== held.submitterName || submitter.type !== held.submitterType ||
          submitter.value !== held.submitterValue || submitter.formNoValidate !== held.submitterNoValidate ||
          submitter.formEnctype !== held.submitterEnctype ||
          submitter.formAction !== held.submitterAction || submitter.formMethod !== held.submitterMethod ||
          submitter.formTarget !== held.submitterTarget)) return;
      replayingForm = form;
      try {
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } catch {
        // Do not fall back to form.submit(), which skips validation and submitter semantics.
      } finally { replayingForm = null; }
    } else {
      if (!held.link.isConnected || held.link.href !== held.destination ||
          (held.link.target && held.link.target !== '_self') || held.link.hasAttribute('download')) return;
      window.location.assign(held.destination);
    }
  };
  ready.then(releaseIntent);
  const hold = (event, next) => {
    // Coalesce every eligible repeat while this intent is held, including settled microtask races.
    if (intent) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!terminal && remaining() <= 0) finish(false, new Error('Cart attribution ready budget exhausted'));
      if (terminal) releaseIntent();
      return;
    }
    if (terminal || !pageActive) return;
    if (remaining() <= 0) {
      finish(false, new Error('Cart attribution ready budget exhausted'));
      return;
    }
    const commerce = commerceSnapshot(next.kind === 'form' ? next.form : null);
    if (commerce === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    intent = { ...next, commerce, page: window.location.href };
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (event.defaultPrevented || !(form instanceof HTMLFormElement) || replayingForm === form ||
        typeof form.requestSubmit !== 'function' || !form.isConnected ||
        (form.target && form.target !== '_self')) return;
    if (submitter && (!(submitter instanceof HTMLElement) || submitter.form !== form ||
        submitter.disabled || !submitter.isConnected ||
        submitter.type !== 'submit' ||
        (submitter.formTarget && submitter.formTarget !== '_self'))) return;
    let action;
    try { action = new URL(submitter?.formAction || form.action, window.location.href); } catch { return; }
    if (action.origin !== window.location.origin) return;
    const isCheckout = submitter?.getAttribute('name') === 'checkout' ||
      /\/checkout\/?$/u.test(action.pathname);
    if (!isCheckout) return;
    hold(event, {
      kind: 'form', form, submitter,
      action: form.action, method: form.method, target: form.target,
      noValidate: form.noValidate, enctype: form.enctype,
      submitterName: submitter?.name, submitterType: submitter?.type,
      submitterValue: submitter?.value, submitterNoValidate: submitter?.formNoValidate,
      submitterEnctype: submitter?.formEnctype,
      submitterAction: submitter?.formAction, submitterMethod: submitter?.formMethod,
      submitterTarget: submitter?.formTarget,
    });
  }, true);

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || !(event.target instanceof Element) ||
        (event.button != null && event.button !== 0) ||
        event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || !link.isConnected || (link.target && link.target !== '_self') ||
        link.hasAttribute('download')) return;
    let destination;
    try { destination = new URL(link.href, window.location.href); } catch { return; }
    if (destination.origin !== window.location.origin || !/\/checkout\/?$/u.test(destination.pathname)) return;
    hold(event, { kind: 'link', link, destination: destination.href });
  }, true);

  window.addEventListener('pagehide', () => {
    pageActive = false;
    intent = null;
    finish(false, new Error('Cart attribution page left'));
  }, { once: true });
})();
